/**
 * Admin Buyers Management Page
 *
 * Complete overview of all buyer companies (Big Pharma)
 * with CRUD operations, email domain management, and user management
 */

import { useState, useEffect } from 'react';
import {
  Building,
  Search,
  MapPin,
  Mail,
  Globe,
  DollarSign,
  Eye,
  MessageSquare,
  Loader2,
  AlertCircle,
  TrendingUp,
  Target,
  X,
  ExternalLink,
  Zap,
  Users,
  Calendar,
  Briefcase,
  Activity,
  Plus,
  Edit2,
  Trash2,
  Save,
  UserPlus,
  UserMinus,
  AtSign,
  Send,
  Clock,
  RefreshCw,
  CheckCircle,
  XCircle
} from 'lucide-react';
import api from '../../services/api';

export default function AdminBuyers() {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

  // CRUD states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // User management states
  const [linkedUsers, setLinkedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [addingUser, setAddingUser] = useState(false);

  // Invitation states
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => {
    fetchBuyers();
  }, [search]);

  const fetchBuyers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/buyers', {
        params: { search, limit: pagination.limit, offset: pagination.offset }
      });
      if (response.data.success) {
        setBuyers(response.data.data);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBuyerDetail = async (id) => {
    try {
      setDetailLoading(true);
      const response = await api.get(`/admin/buyers/${id}`);
      if (response.data.success) {
        setSelectedBuyer(response.data.data);
        setEditData(response.data.data);
        fetchLinkedUsers(id);
        fetchInvitations(id);
      }
    } catch (err) {
      console.error('Failed to fetch buyer details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchLinkedUsers = async (buyerId) => {
    try {
      setLoadingUsers(true);
      const response = await api.get(`/admin/buyers/${buyerId}/users`);
      if (response.data.success) {
        setLinkedUsers(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch linked users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchInvitations = async (buyerId) => {
    try {
      setLoadingInvitations(true);
      const response = await api.get(`/admin/buyers/${buyerId}/invitations`);
      if (response.data.success) {
        setInvitations(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch invitations:', err);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setSendingInvite(true);
    try {
      await api.post('/admin/invitations', {
        email: inviteEmail,
        entity_type: 'buyer',
        entity_id: selectedBuyer.id
      });
      setInviteEmail('');
      fetchInvitations(selectedBuyer.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!confirm('Cancel this invitation?')) return;
    try {
      await api.delete(`/admin/invitations/${inviteId}`);
      fetchInvitations(selectedBuyer.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await api.post(`/admin/invitations/${inviteId}/resend`);
      fetchInvitations(selectedBuyer.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.post('/admin/buyers', editData);
      if (response.data.success) {
        setShowCreateModal(false);
        setEditData({});
        fetchBuyers();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/admin/buyers/${selectedBuyer.id}`, editData);
      if (response.data.success) {
        setSelectedBuyer(response.data.data);
        setEditMode(false);
        fetchBuyers();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete "${selectedBuyer.company_name}"? This will remove all associated data.`)) {
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/buyers/${selectedBuyer.id}`);
      setSelectedBuyer(null);
      fetchBuyers();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail.trim()) return;
    setAddingUser(true);
    try {
      await api.post(`/admin/buyers/${selectedBuyer.id}/users`, { email: newUserEmail });
      setNewUserEmail('');
      fetchLinkedUsers(selectedBuyer.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setAddingUser(false);
    }
  };

  const handleRemoveUser = async (userId, email) => {
    if (!confirm(`Remove ${email} from this buyer?`)) return;
    try {
      await api.delete(`/admin/buyers/${selectedBuyer.id}/users/${userId}`);
      fetchLinkedUsers(selectedBuyer.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatRevenue = (revenue) => {
    if (!revenue) return 'N/A';
    if (typeof revenue === 'number') {
      if (revenue >= 1000) return `$${(revenue / 1000).toFixed(1)}B`;
      return `$${revenue}M`;
    }
    return revenue;
  };

  const getSignalTypeColor = (type) => {
    const colors = {
      'acquisition': 'bg-green-100 text-green-700',
      'partnership': 'bg-blue-100 text-blue-700',
      'licensing': 'bg-purple-100 text-purple-700',
      'investment': 'bg-yellow-100 text-yellow-700',
      'clinical_trial': 'bg-orange-100 text-orange-700',
      'hiring': 'bg-pink-100 text-pink-700',
      'conference': 'bg-gray-100 text-gray-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getBuyerTypeColor = (type) => {
    const colors = {
      'MNC': 'bg-blue-100 text-blue-700',
      'Biotech': 'bg-green-100 text-green-700',
      'CDMO': 'bg-purple-100 text-purple-700',
      'Other': 'bg-gray-100 text-gray-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading && buyers.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Buyer Companies</h1>
          <p className="text-gray-600 mt-1">
            {pagination.total} pharma/biotech buyers in database
          </p>
        </div>
        <button
          onClick={() => { setEditData({}); setShowCreateModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          <Plus className="w-4 h-4" />
          Add Buyer
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by company name, headquarters, BD email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
        />
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Buyers Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {buyers.map((buyer) => (
          <div
            key={buyer.id}
            className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => fetchBuyerDetail(buyer.id)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {buyer.logo_url ? (
                  <img
                    src={buyer.logo_url}
                    alt={buyer.company_name}
                    className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                    <Building className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">{buyer.company_name}</h3>
                  {buyer.company_name_cn && (
                    <p className="text-sm text-gray-500">{buyer.company_name_cn}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {buyer.buyer_type && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getBuyerTypeColor(buyer.buyer_type)}`}>
                    {buyer.buyer_type}
                  </span>
                )}
                {buyer.revenue_usd_millions && (
                  <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                    {formatRevenue(buyer.revenue_usd_millions)}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2 text-sm">
              {buyer.headquarters && (
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{buyer.headquarters}</span>
                </div>
              )}
              {buyer.bd_contact_email && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span className="text-blue-600">{buyer.bd_contact_email}</span>
                </div>
              )}
              {buyer.verified_email_domains && buyer.verified_email_domains.length > 0 && (
                <div className="flex items-center gap-2 text-gray-600">
                  <AtSign className="w-4 h-4" />
                  <span className="text-purple-600">{buyer.verified_email_domains.join(', ')}</span>
                </div>
              )}
            </div>

            {/* Focus Areas */}
            {buyer.focus_therapeutic_areas && buyer.focus_therapeutic_areas.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {buyer.focus_therapeutic_areas.slice(0, 3).map((area, i) => (
                  <span key={i} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                    {area}
                  </span>
                ))}
                {buyer.focus_therapeutic_areas.length > 3 && (
                  <span className="text-xs text-gray-500">+{buyer.focus_therapeutic_areas.length - 3} more</span>
                )}
              </div>
            )}

            {/* Stats Row */}
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-6 text-sm">
              <div className="flex items-center gap-1.5 text-gray-600">
                <Eye className="w-4 h-4" />
                <span>{buyer.profiles_viewed || 0} views</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600">
                <MessageSquare className="w-4 h-4" />
                <span>{buyer.intro_requests || 0} intros</span>
              </div>
              <div className="flex items-center gap-1.5 text-gray-600">
                <Zap className="w-4 h-4" />
                <span>{buyer.signal_count || 0} signals</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {buyers.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          No buyers found matching your search.
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Add New Buyer</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-150px)]">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                  <input
                    type="text"
                    required
                    value={editData.company_name || ''}
                    onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chinese Name</label>
                  <input
                    type="text"
                    value={editData.company_name_cn || ''}
                    onChange={(e) => setEditData({ ...editData, company_name_cn: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Headquarters</label>
                  <input
                    type="text"
                    value={editData.headquarters || ''}
                    onChange={(e) => setEditData({ ...editData, headquarters: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Type</label>
                  <select
                    value={editData.buyer_type || ''}
                    onChange={(e) => setEditData({ ...editData, buyer_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Select type...</option>
                    <option value="MNC">MNC (Multinational)</option>
                    <option value="Biotech">Biotech</option>
                    <option value="CDMO">CDMO</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Domains (comma-separated, e.g., pfizer.com, pfizer.cn)
                </label>
                <input
                  type="text"
                  value={editData.verified_email_domains?.join(', ') || ''}
                  onChange={(e) => setEditData({
                    ...editData,
                    verified_email_domains: e.target.value.split(',').map(d => d.trim()).filter(d => d)
                  })}
                  placeholder="company.com, company.cn"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">Users registering with these domains will auto-link to this buyer</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BD Contact Email</label>
                  <input
                    type="email"
                    value={editData.bd_contact_email || ''}
                    onChange={(e) => setEditData({ ...editData, bd_contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">BD Page URL</label>
                  <input
                    type="url"
                    value={editData.bd_page_url || ''}
                    onChange={(e) => setEditData({ ...editData, bd_page_url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Create Buyer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedBuyer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-4">
                {selectedBuyer.logo_url ? (
                  <img
                    src={selectedBuyer.logo_url}
                    alt={selectedBuyer.company_name}
                    className="w-14 h-14 rounded-xl object-contain bg-gray-50"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
                    <Building className="w-7 h-7 text-white" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedBuyer.company_name}</h2>
                  {selectedBuyer.company_name_cn && (
                    <p className="text-gray-500">{selectedBuyer.company_name_cn}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!editMode ? (
                  <>
                    <button
                      onClick={() => setEditMode(true)}
                      className="p-2 hover:bg-gray-100 rounded-lg text-blue-600"
                      title="Edit"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                      title="Delete"
                    >
                      {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleUpdate}
                      disabled={saving}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save
                    </button>
                    <button
                      onClick={() => { setEditMode(false); setEditData(selectedBuyer); }}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </>
                )}
                <button
                  onClick={() => { setSelectedBuyer(null); setEditMode(false); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-red-600" />
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Editable Fields */}
                  {editMode ? (
                    <div className="space-y-4 bg-yellow-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-yellow-900">Edit Mode</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                          <input
                            type="text"
                            value={editData.company_name || ''}
                            onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Chinese Name</label>
                          <input
                            type="text"
                            value={editData.company_name_cn || ''}
                            onChange={(e) => setEditData({ ...editData, company_name_cn: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Headquarters</label>
                          <input
                            type="text"
                            value={editData.headquarters || ''}
                            onChange={(e) => setEditData({ ...editData, headquarters: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Buyer Type</label>
                          <select
                            value={editData.buyer_type || ''}
                            onChange={(e) => setEditData({ ...editData, buyer_type: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          >
                            <option value="">Select type...</option>
                            <option value="MNC">MNC</option>
                            <option value="Biotech">Biotech</option>
                            <option value="CDMO">CDMO</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Domains (auto-link users)
                        </label>
                        <input
                          type="text"
                          value={editData.verified_email_domains?.join(', ') || ''}
                          onChange={(e) => setEditData({
                            ...editData,
                            verified_email_domains: e.target.value.split(',').map(d => d.trim()).filter(d => d)
                          })}
                          placeholder="pfizer.com, pfizer.cn"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">BD Contact Email</label>
                          <input
                            type="email"
                            value={editData.bd_contact_email || ''}
                            onChange={(e) => setEditData({ ...editData, bd_contact_email: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">BD Page URL</label>
                          <input
                            type="url"
                            value={editData.bd_page_url || ''}
                            onChange={(e) => setEditData({ ...editData, bd_page_url: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Basic Info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-500 mb-1">Headquarters</div>
                          <div className="font-medium flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {selectedBuyer.headquarters || 'N/A'}
                          </div>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-500 mb-1">Revenue</div>
                          <div className="font-medium flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                            {formatRevenue(selectedBuyer.revenue_usd_millions)}
                          </div>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-500 mb-1">Type</div>
                          <div className="font-medium">
                            {selectedBuyer.buyer_type && (
                              <span className={`px-2 py-1 rounded text-sm ${getBuyerTypeColor(selectedBuyer.buyer_type)}`}>
                                {selectedBuyer.buyer_type}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="text-sm text-gray-500 mb-1">Stock</div>
                          <div className="font-medium">{selectedBuyer.stock_ticker || 'Private'}</div>
                        </div>
                      </div>

                      {/* Email Domains */}
                      {selectedBuyer.verified_email_domains && selectedBuyer.verified_email_domains.length > 0 && (
                        <div className="bg-purple-50 p-4 rounded-lg">
                          <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                            <AtSign className="w-5 h-5" />
                            Verified Email Domains
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedBuyer.verified_email_domains.map((domain, i) => (
                              <span key={i} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                                @{domain}
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-purple-700 mt-2">Users with these email domains auto-link to this buyer</p>
                        </div>
                      )}

                      {/* BD Contact Info */}
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                          <Briefcase className="w-5 h-5" />
                          Business Development Contact
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {selectedBuyer.bd_contact_email ? (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-blue-600" />
                              <a
                                href={`mailto:${selectedBuyer.bd_contact_email}`}
                                className="text-blue-800 hover:underline"
                              >
                                {selectedBuyer.bd_contact_email}
                              </a>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-gray-500">
                              <Mail className="w-4 h-4" />
                              <span>No BD email on file</span>
                            </div>
                          )}
                          {selectedBuyer.bd_page_url && (
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-blue-600" />
                              <a
                                href={selectedBuyer.bd_page_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-800 hover:underline flex items-center gap-1"
                              >
                                BD Partnership Page
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Linked Users Section */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Linked User Accounts ({linkedUsers.length})
                    </h3>

                    {loadingUsers ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                      </div>
                    ) : (
                      <>
                        {/* Add User */}
                        <div className="flex gap-2 mb-3">
                          <input
                            type="email"
                            value={newUserEmail}
                            onChange={(e) => setNewUserEmail(e.target.value)}
                            placeholder="user@company.com"
                            className="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          />
                          <button
                            onClick={handleAddUser}
                            disabled={addingUser || !newUserEmail.trim()}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
                          >
                            {addingUser ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                            Add
                          </button>
                        </div>

                        {/* User List */}
                        {linkedUsers.length > 0 ? (
                          <div className="space-y-2">
                            {linkedUsers.map((user) => (
                              <div key={user.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                    <span className="text-sm font-medium text-green-700">
                                      {user.email.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">{user.email}</p>
                                    <p className="text-xs text-gray-500">
                                      Joined {formatDate(user.created_at)}
                                      {user.last_login && ` • Last login ${formatDate(user.last_login)}`}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleRemoveUser(user.id, user.email)}
                                  className="p-1.5 hover:bg-red-50 rounded text-red-600"
                                  title="Remove user"
                                >
                                  <UserMinus className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-green-700">No users linked to this buyer yet.</p>
                        )}
                      </>
                    )}
                  </div>

                  {/* Invitations Section */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Invite New Users
                    </h3>

                    {/* Send Invitation */}
                    <div className="flex gap-2 mb-3">
                      <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        placeholder="Enter email to invite..."
                        className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        onClick={handleSendInvite}
                        disabled={sendingInvite || !inviteEmail.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                      >
                        {sendingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Invite
                      </button>
                    </div>
                    <p className="text-xs text-blue-700 mb-3">User will receive an email with a registration link that auto-links them to this buyer.</p>

                    {/* Pending Invitations */}
                    {loadingInvitations ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                      </div>
                    ) : invitations.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-blue-900">Pending Invitations</p>
                        {invitations.filter(inv => inv.status === 'pending').map((inv) => (
                          <div key={inv.id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <Clock className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{inv.email}</p>
                                <p className="text-xs text-gray-500">
                                  Sent {formatDate(inv.created_at)}
                                  {new Date(inv.expires_at) < new Date() ? (
                                    <span className="text-red-600 ml-1">(Expired)</span>
                                  ) : (
                                    <span className="text-blue-600 ml-1">• Expires {formatDate(inv.expires_at)}</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleResendInvite(inv.id)}
                                className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                                title="Resend invitation"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleCancelInvite(inv.id)}
                                className="p-1.5 hover:bg-red-50 rounded text-red-600"
                                title="Cancel invitation"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {invitations.filter(inv => inv.status === 'accepted').length > 0 && (
                          <>
                            <p className="text-sm font-medium text-green-900 mt-3">Accepted Invitations</p>
                            {invitations.filter(inv => inv.status === 'accepted').map((inv) => (
                              <div key={inv.id} className="flex items-center gap-3 p-2 bg-green-50 rounded-lg">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{inv.email}</p>
                                  <p className="text-xs text-gray-500">Accepted</p>
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-blue-700">No pending invitations.</p>
                    )}
                  </div>

                  {/* Focus Areas */}
                  {!editMode && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedBuyer.focus_therapeutic_areas && selectedBuyer.focus_therapeutic_areas.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            Therapeutic Focus
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedBuyer.focus_therapeutic_areas.map((area, i) => (
                              <span key={i} className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                                {area}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {selectedBuyer.focus_modalities && selectedBuyer.focus_modalities.length > 0 && (
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Modality Focus</h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedBuyer.focus_modalities.map((mod, i) => (
                              <span key={i} className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                {mod}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Acquisition Signals */}
                  {!editMode && selectedBuyer.signals && selectedBuyer.signals.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Activity className="w-5 h-5" />
                        Recent Acquisition Signals ({selectedBuyer.signals.length})
                      </h3>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {selectedBuyer.signals.map((signal) => (
                          <div key={signal.id} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSignalTypeColor(signal.signal_type)}`}>
                                {signal.signal_type?.replace('_', ' ').toUpperCase()}
                              </span>
                              <span className="text-xs text-gray-500">{formatDate(signal.signal_date)}</span>
                            </div>
                            <p className="text-sm text-gray-700">{signal.description}</p>
                            {signal.source_url && (
                              <a
                                href={signal.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"
                              >
                                Source <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Engagement Stats */}
                  {!editMode && (
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{selectedBuyer.signals?.length || 0}</div>
                        <div className="text-sm text-gray-500">Signals</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{selectedBuyer.recentViews?.length || 0}</div>
                        <div className="text-sm text-gray-500">Profiles Viewed</div>
                      </div>
                      <div className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="text-2xl font-bold text-gray-900">{selectedBuyer.introductionRequests?.length || 0}</div>
                        <div className="text-sm text-gray-500">Intro Requests</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
